<template>
  <div>
    <div v-if="loading" class="text-gray-500 p-4 flex items-center gap-2">
      <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Loading patterns...
    </div>
    <div v-else-if="error" class="text-red-600 p-4 bg-red-50 rounded-lg border border-red-200">{{ error }}</div>
    <div v-else class="flex gap-5">
      <!-- Left: Tag Tree (3 layers) -->
      <div class="w-72 shrink-0 bg-white rounded-lg border border-gray-200 max-h-[calc(100vh-200px)] overflow-y-auto">
        <!-- Layer 0: Source Data -->
        <div class="p-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <span class="text-xs font-semibold text-gray-900 uppercase tracking-wide">Layer 0: Source Data</span>
          <span class="text-xs text-gray-500 ml-2">(19 dimensions)</span>
        </div>
        <div class="divide-y divide-gray-100">
          <div v-for="family in patternsData?.families" :key="family.name" class="p-2">
            <div class="text-xs font-semibold text-gray-700 mb-1">{{ family.name }}</div>
            <div class="space-y-0.5">
              <button
                v-for="dim in family.dimensions"
                :key="dim.column"
                @click="$emit('select-dimension', { dim, family: family.name })"
                :class="[
                  'w-full text-left px-2 py-1 rounded text-xs transition-colors',
                  selectedItem?.type === 'dimension' && selectedItem?.column === dim.column
                    ? 'bg-primary-600/10 text-primary-600 font-medium'
                    : 'hover:bg-gray-50 text-gray-500'
                ]"
              >
                {{ dim.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Layer 1: Boolean Flags -->
        <div class="p-2 border-b border-t border-gray-200 bg-gray-50 sticky top-0 z-10">
          <span class="text-xs font-semibold text-gray-900 uppercase tracking-wide">Layer 1: Boolean Flags</span>
          <span class="text-xs text-gray-500 ml-2">({{ patternsData?.layer2Flags?.length }} flags)</span>
        </div>
        <div class="divide-y divide-gray-100">
          <div v-for="category in layer2Categories" :key="category" class="p-2">
            <div class="text-xs font-semibold text-gray-700 mb-1">{{ category }}</div>
            <div class="space-y-0.5">
              <button
                v-for="flag in layer2ByCategory(category)"
                :key="flag.column"
                @click="$emit('select-layer2-flag', flag)"
                :class="[
                  'w-full text-left px-2 py-1 rounded text-xs flex justify-between transition-colors',
                  selectedItem?.type === 'layer2' && selectedItem?.column === flag.column
                    ? 'bg-primary-600/10 text-primary-600 font-medium'
                    : 'hover:bg-gray-50 text-gray-500'
                ]"
              >
                <span>{{ flag.label }}</span>
                <span class="text-gray-600">{{ formatNumber(flag.true_count) }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Layer 2: Archetypes -->
        <div class="p-2 border-b border-t border-gray-200 bg-gray-50 sticky top-0 z-10">
          <span class="text-xs font-semibold text-gray-900 uppercase tracking-wide">Layer 2: Archetypes</span>
          <span class="text-xs text-gray-500 ml-2">({{ patternsData?.layer3Archetypes?.length }} types)</span>
        </div>
        <div class="p-2 space-y-0.5">
          <button
            v-for="archetype in patternsData?.layer3Archetypes"
            :key="archetype.column"
            @click="$emit('select-layer3-archetype', archetype)"
            :class="[
              'w-full text-left px-2 py-1 rounded text-xs flex justify-between transition-colors',
              selectedItem?.type === 'layer3' && selectedItem?.column === archetype.column
                ? 'bg-primary-600/10 text-primary-600 font-medium'
                : 'hover:bg-gray-50 text-gray-500'
            ]"
          >
            <span>{{ archetype.label }}</span>
            <span class="text-gray-600">{{ formatNumber(archetype.true_count) }}</span>
          </button>
        </div>
      </div>

      <!-- Right: Detail Panel -->
      <div class="flex-1">
        <div v-if="!selectedItem" class="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
          </svg>
          <p class="mb-2">Select any tag to see how it's derived</p>
          <p class="text-xs text-gray-600">Layer 0 (Source) → Layer 1 (Flags) → Layer 2 (Archetypes)</p>
        </div>

        <!-- Layer 0: Dimension Detail -->
        <div v-else-if="selectedItem.type === 'dimension'" class="space-y-4">
          <div class="bg-white rounded-lg border border-gray-200 p-4 border-l-4 border-l-primary-500">
            <div class="text-xs text-primary-600 uppercase font-semibold mb-1">Layer 0: Source Data</div>
            <h3 class="text-lg font-semibold text-gray-900">{{ selectedItem.label }}</h3>
            <p class="text-sm text-gray-500">From family: {{ selectedItem.family }}</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4">
            <h4 class="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">Value Distribution</h4>
            <div class="space-y-2">
              <div v-for="val in selectedItem.values" :key="val.value" class="flex items-center gap-3">
                <div class="flex-1 bg-gray-200 rounded-full h-5 overflow-hidden">
                  <div
                    class="h-full bg-primary-600 rounded-full transition-all duration-500"
                    :style="{ width: (val.count / patternsData.total_practices * 100) + '%' }"
                  ></div>
                </div>
                <span class="text-sm text-gray-700 w-32 truncate">{{ formatTagValue(val.value) }}</span>
                <span class="text-sm text-gray-500 w-16 text-right tabular-nums">{{ formatNumber(val.count) }}</span>
                <span class="text-xs text-gray-600 w-12 text-right tabular-nums">{{ (val.count / patternsData.total_practices * 100).toFixed(1) }}%</span>
              </div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4">
            <h4 class="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">Used by Layer 1 Flags</h4>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="flag in getLayer2UsingDimension(selectedItem.column)"
                :key="flag.column"
                @click="$emit('select-layer2-flag', flag)"
                class="px-2 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded text-sm cursor-pointer hover:bg-primary-600/10 hover:text-primary-600 hover:border-primary-400 transition-colors"
              >
                {{ flag.label }}
              </span>
              <span v-if="getLayer2UsingDimension(selectedItem.column).length === 0" class="text-gray-600 text-sm">None</span>
            </div>
          </div>
        </div>

        <!-- Layer 1: Boolean Flag Detail -->
        <div v-else-if="selectedItem.type === 'layer2'" class="space-y-4">
          <div class="bg-white rounded-lg border border-gray-200 p-4 border-l-4 border-l-primary-500">
            <div class="flex justify-between items-start">
              <div>
                <div class="text-xs text-primary-600 uppercase font-semibold mb-1">Layer 1: Boolean Flag</div>
                <h3 class="text-lg font-semibold text-gray-900">{{ selectedItem.label }}</h3>
                <p class="text-sm text-gray-500">Category: {{ selectedItem.category }}</p>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold text-primary-600 tabular-nums">{{ formatNumber(selectedItem.true_count) }}</div>
                <div class="text-xs text-gray-500">{{ (selectedItem.true_count / patternsData.total_practices * 100).toFixed(1) }}% of practices</div>
              </div>
            </div>
          </div>

          <!-- Rule Editor -->
          <RuleEditor
            :item="selectedItem"
            :ruleEditor="ruleEditor"
            :patternsData="patternsData"
            type="layer2"
            @start-editing="$emit('start-editing-rule', selectedItem)"
            @cancel-editing="$emit('cancel-editing-rule')"
            @preview="$emit('preview-rule')"
            @save="$emit('save-rule')"
            @update-expression="$emit('update-expression', $event)"
            @select-dimension="selectDimensionByColumn"
          />

          <div class="bg-white rounded-lg border border-gray-200 p-4">
            <h4 class="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">Used by Layer 2 Archetypes</h4>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="arch in getLayer3UsingFlag(selectedItem.column)"
                :key="arch.column"
                @click="$emit('select-layer3-archetype', arch)"
                class="px-2 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded text-sm cursor-pointer hover:bg-primary-600/10 hover:text-primary-600 hover:border-primary-400 transition-colors"
              >
                {{ arch.label }}
              </span>
              <span v-if="getLayer3UsingFlag(selectedItem.column).length === 0" class="text-gray-600 text-sm">None</span>
            </div>
          </div>

          <!-- Practice List -->
          <PracticeListSection
            :practices="tagPractices.practices"
            :total="tagPractices.total"
            :loading="tagPractices.loading"
            @load-more="$emit('load-more-practices')"
            @navigate="$emit('navigate-to-practice', $event)"
          />
        </div>

        <!-- Layer 2: Archetype Detail -->
        <div v-else-if="selectedItem.type === 'layer3'" class="space-y-4">
          <div class="bg-white rounded-lg border border-gray-200 p-4 border-l-4 border-l-primary-500">
            <div class="flex justify-between items-start">
              <div>
                <div class="text-xs text-primary-600 uppercase font-semibold mb-1">Layer 2: Archetype</div>
                <h3 class="text-lg font-semibold text-gray-900">{{ selectedItem.label }}</h3>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold text-primary-600 tabular-nums">{{ formatNumber(selectedItem.true_count) }}</div>
                <div class="text-xs text-gray-500">{{ (selectedItem.true_count / patternsData.total_practices * 100).toFixed(1) }}% of practices</div>
              </div>
            </div>
          </div>

          <!-- Rule Editor -->
          <RuleEditor
            :item="selectedItem"
            :ruleEditor="ruleEditor"
            :patternsData="patternsData"
            type="layer3"
            @start-editing="$emit('start-editing-rule', selectedItem)"
            @cancel-editing="$emit('cancel-editing-rule')"
            @preview="$emit('preview-rule')"
            @save="$emit('save-rule')"
            @update-expression="$emit('update-expression', $event)"
            @select-flag="selectLayer2FlagByColumn"
          />

          <!-- Practice List -->
          <PracticeListSection
            :practices="tagPractices.practices"
            :total="tagPractices.total"
            :loading="tagPractices.loading"
            @load-more="$emit('load-more-practices')"
            @navigate="$emit('navigate-to-practice', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RuleEditor from './RuleEditor.vue'
import PracticeListSection from './PracticeListSection.vue'

export default {
  name: 'PatternExplorerTab',
  components: {
    RuleEditor,
    PracticeListSection,
  },
  props: {
    patternsData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    selectedItem: {
      type: Object,
      default: null,
    },
    ruleEditor: {
      type: Object,
      required: true,
    },
    tagPractices: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'select-dimension',
    'select-layer2-flag',
    'select-layer3-archetype',
    'start-editing-rule',
    'cancel-editing-rule',
    'preview-rule',
    'save-rule',
    'update-expression',
    'load-more-practices',
    'navigate-to-practice',
  ],
  computed: {
    layer2Categories() {
      if (!this.patternsData?.layer2Flags) return []
      return [...new Set(this.patternsData.layer2Flags.map(f => f.category))]
    },
  },
  methods: {
    formatNumber(num) {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num?.toLocaleString() || '0'
    },
    formatTagValue(value) {
      if (!value) return '—'
      return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    layer2ByCategory(category) {
      return (this.patternsData?.layer2Flags || []).filter(f => f.category === category)
    },
    getLayer2UsingDimension(column) {
      return (this.patternsData?.layer2Flags || []).filter(f => (f.layer1_tags || []).includes(column))
    },
    getLayer3UsingFlag(column) {
      return (this.patternsData?.layer3Archetypes || []).filter(a => (a.layer2_flags || []).includes(column))
    },
    selectDimensionByColumn(column) {
      for (const family of this.patternsData?.families || []) {
        const dim = family.dimensions.find(d => d.column === column)
        if (dim) {
          this.$emit('select-dimension', { dim, family: family.name })
          return
        }
      }
    },
    selectLayer2FlagByColumn(column) {
      const flag = this.patternsData?.layer2Flags?.find(f => f.column === column)
      if (flag) this.$emit('select-layer2-flag', flag)
    },
  },
}
</script>
